import React, { useState, useEffect, useCallback } from 'react';
import {
  Chip,
  Popover,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Button,
  InputAdornment,
  CircularProgress,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Folders, Plus } from '@phosphor-icons/react';
import { useTheme } from '@mui/material/styles';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import {
  getCollectionsByCompany,
  addSessionToCollection,
  removeSessionFromCollection,
  createCollection
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import { Client } from '@langchain/langgraph-sdk';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';

/**
 * Helper function to acquire Auth0 access token
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Interactive token acquisition failed.');
      }
      return token;
    }
    throw error;
  }
};

interface SessionCollectionTaggerProps {
  sessionId: string;
  currentCollections: SessionCollection[];
  onUpdate: () => void;
}

export const SessionCollectionTagger: React.FC<
  SessionCollectionTaggerProps
> = ({ sessionId, currentCollections, onUpdate }) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [allCollections, setAllCollections] = useState<SessionCollection[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = useState('');
  const [creating, setCreating] = useState(false);

  // Local optimistic state for current collections
  const [optimisticCollectionIds, setOptimisticCollectionIds] = useState<
    Set<string>
  >(new Set());

  const open = Boolean(anchorEl);

  // Sync optimistic state with props
  useEffect(() => {
    setOptimisticCollectionIds(new Set(currentCollections.map((c) => c.id)));
  }, [currentCollections]);

  // Use optimistic state for UI
  const currentCollectionIds = Array.from(optimisticCollectionIds);

  // Compute optimistic collections for display (merge optimistic IDs with actual collection data)
  const optimisticCollections = React.useMemo(() => {
    // Get all collections that should be shown based on optimistic state
    const collectionMap = new Map(currentCollections.map((c) => [c.id, c]));

    // Also check allCollections for newly added ones
    allCollections.forEach((c) => {
      if (!collectionMap.has(c.id)) {
        collectionMap.set(c.id, c);
      }
    });

    return Array.from(optimisticCollectionIds)
      .map((id) => collectionMap.get(id))
      .filter((c): c is SessionCollection => c !== undefined);
  }, [optimisticCollectionIds, currentCollections, allCollections]);

  // Load all collections when popover opens
  const loadCollections = useCallback(async () => {
    if (!selectedUserRole || !appUser) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        1,
        100,
        searchTerm || undefined
      );
      setAllCollections(data);
    } catch (err) {
      console.error('Error loading collections:', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [
    selectedUserRole,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    searchTerm
  ]);

  useEffect(() => {
    if (open) {
      loadCollections();
    }
  }, [open, loadCollections]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const timeout = setTimeout(() => {
      loadCollections();
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, open]);

  // Trigger collection summary agent (non-blocking background operation)
  const triggerCollectionSummary = useCallback(
    async (collectionId: string) => {
      if (!appUser) return;

      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const client = new Client({
          apiUrl: LANGGRAPH_BASE_URL,
          defaultHeaders: {
            Authorization: `Bearer ${token}`,
            'X-User-Id': appUser.id
          }
        });

        // Create a new thread for this collection summary run
        const thread = await client.threads.create();

        console.log(
          'Triggering collection summary agent for collection:',
          collectionId
        );

        // Trigger the collection_summary_flow
        const stream = client.runs.stream(
          thread.thread_id,
          'collection_summary_flow',
          {
            input: {
              messages: [],
              collection_id: collectionId
            },
            config: {
              configurable: {
                auth_token: token,
                collection_id: collectionId,
                user_id: appUser.id
              }
            },
            metadata: {
              collection_id: collectionId,
              user_id: appUser.id,
              thread_id: thread.thread_id
            },
            streamMode: 'updates'
          }
        );

        // Run the stream consumption in the background (don't await)
        // The stream MUST be consumed for the agent to actually run
        (async () => {
          try {
            // Consume the stream
            for await (const _chunk of stream as any) {
              // Just consume the stream, don't need to do anything with chunks
            }
            console.log('Collection summary completed for:', collectionId);
          } catch (err) {
            console.error('Collection summary stream error:', err);
          }
        })();
      } catch (err) {
        console.error('Error triggering collection summary:', err);
        // Don't show error to user - this is a background operation
      }
    },
    [appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const handleToggleCollection = async (collectionId: string) => {
    if (!appUser || updating) return;

    const isCurrentlyInCollection = currentCollectionIds.includes(collectionId);
    setUpdating(collectionId);
    setError(null);

    // Optimistically update UI immediately
    setOptimisticCollectionIds((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlyInCollection) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });

    try {
      if (isCurrentlyInCollection) {
        await removeSessionFromCollection(
          collectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      } else {
        await addSessionToCollection(
          collectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Trigger parent update (will sync with server state)
      onUpdate();

      // Trigger collection summary agent in the background (non-blocking)
      triggerCollectionSummary(collectionId).catch((err) => {
        console.error('Error triggering collection summary:', err);
        // Don't show error to user - this is a background operation
      });
    } catch (err) {
      console.error('Error toggling collection:', err);
      setError('Failed to update collection');

      // Revert optimistic update on error
      setOptimisticCollectionIds((prev) => {
        const newSet = new Set(prev);
        if (isCurrentlyInCollection) {
          newSet.add(collectionId);
        } else {
          newSet.delete(collectionId);
        }
        return newSet;
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateCollection = async () => {
    if (!appUser || !selectedUserRole || !newCollectionTitle.trim() || creating)
      return;

    setCreating(true);
    setError(null);

    try {
      // Create collection and automatically link this session to it
      const newCollection = await createCollection(
        {
          title: newCollectionTitle.trim(),
          description: '',
          referenceNumber: '',
          companyId: selectedUserRole.companyId,
          transcriptionSessionId: sessionId // Backend will automatically link the session
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Optimistically add to current collections immediately
      if (newCollection.id) {
        setOptimisticCollectionIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(newCollection.id);
          return newSet;
        });
      }

      // Reset and reload
      setNewCollectionTitle('');
      setShowCreateDialog(false);
      await loadCollections();
      onUpdate();

      // Trigger collection summary agent in the background (non-blocking)
      if (newCollection.id) {
        triggerCollectionSummary(newCollection.id).catch((err) => {
          console.error('Error triggering collection summary:', err);
          // Don't show error to user - this is a background operation
        });
      }
    } catch (err) {
      console.error('Error creating collection:', err);
      setError('Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  const filteredCollections = allCollections.filter((collection) =>
    searchTerm
      ? collection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        collection.referenceNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true
  );

  const hasCollections = optimisticCollections.length > 0;

  return (
    <>
      <Chip
        icon={hasCollections ? undefined : <Plus size={16} weight="regular" />}
        label={
          optimisticCollections.length === 0
            ? 'Add to Folder'
            : optimisticCollections.length === 1
            ? optimisticCollections[0].title
            : `${optimisticCollections.length} Folders`
        }
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant={hasCollections ? undefined : 'outlined'}
        size="small"
        sx={{
          cursor: 'pointer',
          maxWidth: '200px',
          px: hasCollections ? 1 : 1.5,
          borderRadius: hasCollections ? '4px' : '6px',
          fontSize: hasCollections ? '12px' : '13px',
          height: '24px',
          // When tagged: informational badge style (matches file type chip)
          ...(hasCollections && {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.06)'
                : '#f5f5f5',
            color: theme.palette.text.secondary,
            border: 'none'
          }),
          // When not tagged: outlined button style
          ...(!hasCollections && {
            border: `1px solid ${theme.palette.divider}`
          }),
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            px: hasCollections ? 0.75 : 1,
            fontSize: hasCollections ? '12px' : '13px',
            fontWeight: 500
          },
          '& .MuiChip-icon': {
            ml: 0.5
          },
          '&:hover': {
            backgroundColor: hasCollections
              ? theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : '#e5e5e5'
              : theme.palette.action.hover
          }
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setShowCreateDialog(false);
          setNewCollectionTitle('');
          setError(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxWidth: 400,
            maxHeight: 500,
            mt: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px'
          }
        }}
      >
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: '15px', color: 'text.primary' }}
            >
              Add to Folder
            </Typography>
            <CloseIcon
              fontSize="small"
              sx={{
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
              onClick={() => setAnchorEl(null)}
            />
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Create New Collection Form */}
          {showCreateDialog ? (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Collection title..."
                value={newCollectionTitle}
                onChange={(e) => setNewCollectionTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCollection();
                  }
                }}
                autoFocus
                disabled={creating}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '13px',
                    borderRadius: '6px'
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleCreateCollection}
                  disabled={!newCollectionTitle.trim() || creating}
                  fullWidth
                  sx={{
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    textTransform: 'none',
                    py: 1
                  }}
                >
                  {creating ? 'Creating...' : 'Create & Add'}
                </Button>
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewCollectionTitle('');
                  }}
                  disabled={creating}
                  sx={{
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    textTransform: 'none',
                    py: 1,
                    borderColor: theme.palette.divider,
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: theme.palette.divider,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : '#f9fafb'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search folders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 18, color: 'text.secondary' }}
                      />
                    </InputAdornment>
                  )
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '13px',
                    borderRadius: '6px'
                  }
                }}
              />

              {/* Collections List */}
              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : filteredCollections.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">
                    {searchTerm ? 'No folders found' : 'No folders yet'}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 280, overflow: 'auto', p: 0, mb: 2 }}>
                  {filteredCollections.map((collection) => {
                    const isChecked = currentCollectionIds.includes(
                      collection.id
                    );
                    const isUpdating = updating === collection.id;

                    return (
                      <ListItem
                        key={collection.id}
                        dense
                        onClick={() =>
                          !isUpdating && handleToggleCollection(collection.id)
                        }
                        sx={{
                          borderRadius: '6px',
                          mb: 0.5,
                          py: 1,
                          px: 1.5,
                          cursor: isUpdating ? 'not-allowed' : 'pointer',
                          opacity: isUpdating ? 0.5 : 1,
                          transition: 'background-color 0.15s ease',
                          '&:hover': {
                            backgroundColor: isUpdating
                              ? 'transparent'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#f9fafb'
                          }
                        }}
                      >
                        <Checkbox
                          edge="start"
                          checked={isChecked}
                          disabled={isUpdating}
                          size="small"
                          sx={{ mr: 1.5, p: 0 }}
                        />
                        <ListItemText
                          primary={collection.title}
                          secondary={collection.referenceNumber}
                          primaryTypographyProps={{
                            sx: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '13px',
                              fontWeight: 500,
                              color: 'text.primary'
                            }
                          }}
                          secondaryTypographyProps={{
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary'
                            }
                          }}
                        />
                        {isUpdating && (
                          <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {/* Create New Button */}
              <Button
                fullWidth
                size="medium"
                startIcon={<Plus size={16} weight="bold" />}
                onClick={() => setShowCreateDialog(true)}
                variant="outlined"
                sx={{
                  borderRadius: '6px',
                  borderColor: theme.palette.divider,
                  color: 'text.primary',
                  fontSize: '13px',
                  fontWeight: 500,
                  textTransform: 'none',
                  py: 1,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f9fafb',
                    borderColor: theme.palette.divider
                  }
                }}
              >
                Create New Folder
              </Button>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};
